import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/client';
import type { Project, ModuleCode } from '../api/types';
import { AssetGrid } from '../components/asset-grid';
import { PortfolioDashboard } from '../components/portfolio-dashboard';
import { ExecutiveCockpit } from '../components/executive-cockpit';
import { CryptoArtifactsPanel } from '../components/crypto-artifacts-panel';
import { CompliancePanel } from '../components/compliance-panel';
import { GapAssessmentPanel } from '../components/gap-assessment-panel';
import { VendorReadinessPanel } from '../components/vendor-readiness-panel';
import { Modal } from '../components/admin-ui';
import { useAuth } from '../auth/AuthContext';
import { AssetImportWizard } from '../components/asset-import-wizard';
import { ProjectMembersPanel } from '../components/project-members-panel';  // ← ADDED Phase 1
import { RemediationPanel } from '../components/remediation-panel';
import { OrgStructurePanel } from '../components/org-structure-panel';
import { ReportingProfileSettings } from '../components/reporting-profile-settings';
import { ProgrammeAssumptionsSettings } from '../components/programme-assumptions-settings';
import { moduleLabel, orderModules, MODULE_DISPLAY_ORDER, FOUNDATION_MODULES, ADVANCED_MODULES } from '../lib/riskVocabulary';
import type { InsightDocument } from '../api/insights';

// =============================================================================
// Slice 6b: Project detail page redesign.
//
// Tabs: Overview (dashboard) · Assets (grid) · Runs · Settings
// Tab state lives in the URL (?tab=...) so it's bookmarkable and the
// browser back button works.
//
// Phase 1 change: Added 'members' tab for project-level access control.
// =============================================================================

type Tab = 'overview' | 'remediation' | 'assets' | 'cryptography' | 'compliance' | 'gap-assessment' | 'vendor-readiness' | 'runs' | 'members' | 'settings';
// Step 2 (C-019): Remediation is migration planning, outside the risk assessment; it sits after Runs.
const VALID_TABS: Tab[] = ['overview', 'assets', 'cryptography', 'compliance', 'gap-assessment', 'vendor-readiness', 'runs', 'remediation', 'members', 'settings'];

function isValidTab(s: string | null): s is Tab {
  return s !== null && (VALID_TABS as string[]).includes(s);
}

interface AssessmentRun {
  id: string;
  run_type: string;
  modules_run: string[];
  engine_version: string;
  started_at: string;
  completed_at: string | null;
  status: 'Running' | 'Completed' | 'Failed';
  label: string | null;
  is_pinned: boolean;
  summary: {
    asset_count?: number;
    risk_level_distribution?: Record<string, number>;
  } | null;
}

export function ProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const requestedTab: Tab = isValidTab(tabParam) ? tabParam : 'assets';

  const projectQ = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.get<Project>(`/projects/${projectId}`),
    enabled: !!projectId,
  });

  const [pendingReview, setPendingReview] = useState<{
    total: number; defaults: number; reviewed: number; custom: number;
  } | null>(null);

  const [importWizardOpen, setImportWizardOpen] = useState(false);

  const triggerRun = useMutation({
    mutationFn: (opts: { confirm_defaults?: boolean } = {}) =>
      api.post<{ run_id: string }>(`/projects/${projectId}/runs`, opts),
    onSuccess: (run) => {
      qc.invalidateQueries({ queryKey: ['project', projectId] });
      qc.invalidateQueries({ queryKey: ['projects', projectId] });
      navigate(`/runs/${run.run_id}/results`);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as
          | { error?: { details?: {
              requires_confirmation?: boolean;
              review_summary?: { total: number; defaults: number; reviewed: number; custom: number };
            } } }
          | undefined;
        const summary = body?.error?.details?.review_summary;
        if (summary) {
          setPendingReview(summary);
          return;
        }
      }
    },
  });

  if (projectQ.isLoading) return <p className="p-8 text-ink-500">Loading project…</p>;
  if (projectQ.error) return <p className="p-8 text-accent">Project not found.</p>;
  if (!projectQ.data) return null;

  const project = projectQ.data;
  const moduleScope = Object.entries(project.module_scope ?? {})
    .filter(([, enabled]) => enabled)
    .map(([code]) => code) as ModuleCode[];
  // Vendor readiness is an optional module (C-107): its tab shows only when the project turns it on.
  const vendorReadinessOn = project.vendor_readiness_enabled === true;
  const visibleTabs = VALID_TABS.filter((t) => t !== 'vendor-readiness' || vendorReadinessOn);
  const tab: Tab = visibleTabs.includes(requestedTab) ? requestedTab : 'assets';
  // A risk assessment includes all 7 dimensions and both composites (C-110); older projects may not.
  const missingModules = MODULE_DISPLAY_ORDER.filter((c) => !moduleScope.includes(c as ModuleCode));
  const setTab = (next: Tab) => setSearchParams({ tab: next }, { replace: true });

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <header className="px-6 pt-4 pb-0 border-b border-ink-100 bg-white">
        <div className="mb-2">
          <Link to="/" className="text-xs text-ink-500 hover:text-ink-900">
            ← All projects
          </Link>
        </div>
        <div className="flex items-baseline justify-between gap-6 mb-3">
          <div>
            <h1 className="font-display text-2xl text-ink-900">{project.name}</h1>
            <div className="text-xs text-ink-500 mt-1 tabular-nums">
              {project.project_level === 'Business_Unit' ? 'Business unit' : project.project_level}{project.status !== 'Draft' && project.status !== 'Active' ? ` · ${STATUS_LABEL[project.status] ?? project.status}` : ''} ·{' '}
              {/* The seven scored dimensions; the two composites are not counted (N-13). */}
              {(() => {
                const dims = [...FOUNDATION_MODULES, ...ADVANCED_MODULES];
                const n = moduleScope.filter((m) => (dims as readonly string[]).includes(m)).length;
                return n === dims.length ? `${n} dimensions (all in scope)` : `${n} of ${dims.length} dimensions in scope`;
              })()}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Asset entry belongs to the Assets tab (C-028). */}
            {tab === 'assets' && (
              <>
                <button
                  type="button"
                  onClick={() => setImportWizardOpen(true)}
                  className="btn-secondary text-xs"
                >
                  Import CSV / Excel
                </button>
                <Link
                  to={`/projects/${project.id}/assets/bulk-create`}
                  className="btn-secondary text-xs no-underline"
                >
                  Bulk add
                </Link>
                <Link
                  to={`/projects/${project.id}/assets/new`}
                  className="btn-secondary text-xs no-underline"
                >
                  + Add asset
                </Link>
              </>
            )}
            <button
              type="button"
              onClick={() => triggerRun.mutate({})}
              disabled={triggerRun.isPending}
              className="btn-primary text-xs"
            >
              {triggerRun.isPending ? 'Running…' : 'Run assessment'}
            </button>
          </div>
        </div>

        <nav className="flex gap-6 -mb-px">
          {visibleTabs.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`pb-2 text-sm border-b-2 transition-colors ${
                t === tab
                  ? 'border-accent text-ink-900 font-medium'
                  : 'border-transparent text-ink-500 hover:text-ink-700'
              }`}
            >
              {tabLabel(t)}
            </button>
          ))}
        </nav>

        {triggerRun.error && !(triggerRun.error instanceof ApiError && triggerRun.error.status === 409) && (
          <div className="mt-2 text-xs text-accent">
            Run failed: {(triggerRun.error as Error).message}
          </div>
        )}
        {missingModules.length > 0 && (
          <div role="alert" data-testid="missing-modules-banner" className="mt-3 mb-3 border border-amber-200 bg-amber-50 text-amber-900 text-xs rounded-sm px-3 py-2">
            This project leaves out {missingModules.map(moduleLabel).join(', ')}; Overall risk and
            priority are calculated without them.
          </div>
        )}
      </header>

      <Modal
        open={pendingReview !== null}
        onClose={() => setPendingReview(null)}
        title="Review threat selections before running"
        widthClass="max-w-lg w-full"
      >
        {pendingReview && (
          <>
            <p className="text-sm text-ink-700 mb-3">
              Some assets in this project are using auto-applied default threat compositions
              and have not been reviewed. Running the assessment now will score them against
              all system-shipped compositions matching their asset type.
            </p>
            <div className="grid grid-cols-3 gap-2 my-4 text-xs">
              <div className="border border-ink-200 rounded-sm px-3 py-2 text-center">
                <div className="text-ink-500 uppercase tracking-wide text-[10px]">Defaults</div>
                <div className="text-amber-700 font-display text-2xl">{pendingReview.defaults}</div>
              </div>
              <div className="border border-ink-200 rounded-sm px-3 py-2 text-center">
                <div className="text-ink-500 uppercase tracking-wide text-[10px]">Reviewed</div>
                <div className="text-ink-900 font-display text-2xl">{pendingReview.reviewed}</div>
              </div>
              <div className="border border-ink-200 rounded-sm px-3 py-2 text-center">
                <div className="text-ink-500 uppercase tracking-wide text-[10px]">Total</div>
                <div className="text-ink-900 font-display text-2xl">{pendingReview.total}</div>
              </div>
            </div>
            <p className="text-xs text-ink-500 mb-5">
              Click an asset's Threat cell on the Assets tab to review its compositions.
              Assets on defaults are marked with an amber dot.
            </p>
            <div className="flex justify-end gap-2 border-t border-ink-100 pt-4">
              <button
                type="button"
                onClick={() => setPendingReview(null)}
                className="text-sm px-4 py-1 border border-ink-200 rounded-sm hover:bg-ink-50"
                disabled={triggerRun.isPending}
              >
                Cancel and review
              </button>
              <button
                type="button"
                onClick={() => {
                  setPendingReview(null);
                  triggerRun.mutate({ confirm_defaults: true });
                }}
                disabled={triggerRun.isPending}
                className="text-sm px-4 py-1 bg-accent text-white border border-accent rounded-sm hover:bg-accent/90 disabled:opacity-50"
              >
                Run anyway
              </button>
            </div>
          </>
        )}
      </Modal>

      <AssetImportWizard
        open={importWizardOpen}
        onClose={() => setImportWizardOpen(false)}
        projectId={project.id}
      />

      <main className={`flex-1 min-h-0 bg-[#fbfaf7] ${tab === 'assets' ? 'flex flex-col' : 'overflow-y-auto'}`}>
        {tab === 'overview' && (
          <div className="p-6 max-w-7xl space-y-10">
            <ExecutiveCockpit projectId={project.id} onOpenBacklog={() => setTab('remediation')} />
            <section aria-label="Detailed statistics" className="border-t border-ink-100 pt-6">
              <h2 className="font-display text-lg font-semibold text-ink-800 mb-1">Detailed statistics</h2>
              <p className="text-xs text-ink-500 mb-2">Score distributions by dimension and business unit, from the latest completed run.</p>
              <PortfolioDashboard projectId={project.id} />
            </section>
          </div>
        )}
        {tab === 'remediation' && <RemediationPanel projectId={project.id} />}
        {tab === 'assets' && <AssetGrid projectId={project.id} moduleScope={moduleScope} />}
        {tab === 'cryptography' && (
          <div>
            <div className="max-w-7xl mx-auto px-6 py-6">
              <CryptoArtifactsPanel projectId={project.id} />
            </div>
          </div>
        )}
        {tab === 'compliance' && (
          <div>
            <div className="max-w-7xl mx-auto px-6 py-6">
              <CompliancePanel projectId={project.id} />
            </div>
          </div>
        )}
        {tab === 'gap-assessment' && (
          <div>
            <div className="px-6 py-6">
              <GapAssessmentPanel projectId={project.id} />
            </div>
          </div>
        )}
        {tab === 'vendor-readiness' && (
          <div>
            <div className="max-w-7xl mx-auto px-6 py-6">
              <VendorReadinessPanel projectId={project.id} />
            </div>
          </div>
        )}
        {tab === 'runs' && <div className="overflow-y-auto"><RunsList projectId={project.id} /></div>}
        {tab === 'members' && <ProjectMembersPanel projectId={project.id} />}
        {tab === 'settings' && <SettingsPanel project={project} />}
      </main>
    </div>
  );
}

function tabLabel(t: Tab): string {
  if (t === 'gap-assessment') return 'Organisational readiness';
  if (t === 'remediation') return 'Migration planning (optional)';
  if (t === 'vendor-readiness') return 'Vendor readiness';
  if (t === 'members') return 'Members';
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** Human run type: one module, or every module in the project's scope (C-026). */
function runTypeLabel(r: AssessmentRun): string {
  return r.run_type === 'Single_module' ? 'Single module' : 'Full assessment';
}

function RunsList({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const runsQ = useQuery({
    queryKey: ['project-runs', projectId],
    queryFn: () => api.get<AssessmentRun[]>(`/projects/${projectId}/runs`),
  });
  // Same document the Overview reads: the latest completed run's headline figures (C-026).
  const latest = useQuery({
    queryKey: ['project-insights', projectId],
    queryFn: () => api.get<InsightDocument>(`/projects/${projectId}/insights/latest`),
    retry: false,
  });

  const [editingLabel, setEditingLabel] = useState<string | null>(null);
  const [labelDraft, setLabelDraft] = useState('');

  const patchRun = useMutation({
    mutationFn: ({ runId, patch }: { runId: string; patch: { label?: string | null; is_pinned?: boolean } }) =>
      api.patch(`/projects/${projectId}/runs/${runId}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-runs', projectId] });
    },
  });

  function handleLabelSave(runId: string) {
    patchRun.mutate({ runId, patch: { label: labelDraft.trim() || null } });
    setEditingLabel(null);
  }

  function handlePinToggle(run: AssessmentRun) {
    patchRun.mutate({ runId: run.id, patch: { is_pinned: !run.is_pinned } });
  }

  if (runsQ.isLoading) return <div className="p-8 text-ink-500 text-sm">Loading runs…</div>;
  if (runsQ.error) return <div className="p-8 text-accent text-sm">Failed to load runs.</div>;
  const runs = runsQ.data ?? [];

  if (runs.length === 0) {
    return (
      <div className="p-12 text-center">
        <p className="text-ink-500 text-sm">No assessment runs yet.</p>
      </div>
    );
  }

  const pinned = runs.filter((r) => r.is_pinned);
  const unpinned = runs.filter((r) => !r.is_pinned);

  const RunRow = ({ r }: { r: AssessmentRun }) => (
    <tr key={r.id} className={`border-t border-ink-100 hover:bg-ink-50/50 ${r.is_pinned ? 'bg-amber-50/30' : ''}`}>
      <td className="px-3 py-2 w-8 text-center">
        {r.status === 'Completed' && (
          <button
            type="button"
            title={r.is_pinned ? 'Unpin this run' : 'Pin this run as a saved snapshot'}
            onClick={() => handlePinToggle(r)}
            className={`text-sm transition-colors ${r.is_pinned ? 'text-amber-500 hover:text-amber-700' : 'text-ink-200 hover:text-amber-400'}`}
          >
            {r.is_pinned ? '★' : '☆'}
          </button>
        )}
      </td>
      <td className="px-3 py-2">
        {editingLabel === r.id ? (
          <div className="flex items-center gap-1">
            <input
              type="text"
              value={labelDraft}
              onChange={(e) => setLabelDraft(e.target.value)}
              placeholder="e.g. Q1 2026 Baseline"
              className="border border-accent rounded-sm px-2 py-0.5 text-xs flex-1 focus:outline-none"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleLabelSave(r.id);
                if (e.key === 'Escape') setEditingLabel(null);
              }}
            />
            <button
              type="button"
              onClick={() => handleLabelSave(r.id)}
              className="text-[11px] px-2 py-0.5 bg-accent text-white rounded-sm"
            >Save</button>
            <button
              type="button"
              onClick={() => setEditingLabel(null)}
              className="text-[11px] px-2 py-0.5 border border-ink-200 rounded-sm text-ink-500"
            >×</button>
          </div>
        ) : (
          <div>
            {r.label ? (
              <div className="font-medium text-ink-800 text-xs mb-0.5">{r.label}</div>
            ) : null}
            <div className="font-mono tabular-nums text-xs text-ink-500">
              {new Date(r.started_at).toLocaleString()}
            </div>
            {r.status === 'Completed' && (
              <button
                type="button"
                onClick={() => { setLabelDraft(r.label ?? ''); setEditingLabel(r.id); }}
                className="text-[10px] text-ink-300 hover:text-accent mt-0.5"
              >
                {r.label ? 'rename' : '+ add label'}
              </button>
            )}
          </div>
        )}
      </td>
      <td className="px-3 py-2"><RunStatusBadge status={r.status} /></td>
      <td className="px-3 py-2 text-xs text-ink-700 whitespace-nowrap">{runTypeLabel(r)}</td>
      <td className="px-3 py-2 text-xs text-ink-500 truncate max-w-[260px]" title={orderModules(r.modules_run).map(moduleLabel).join(', ')}>
        {orderModules(r.modules_run).map(moduleLabel).join(', ')}
      </td>
      <td className="px-3 py-2 text-xs text-ink-700 font-mono tabular-nums text-right">
        {r.summary?.asset_count ?? '—'}
      </td>
      <td className="px-3 py-2 text-right">
        {r.status === 'Completed' && (
          <Link to={`/runs/${r.id}/results`} className="text-xs text-accent hover:underline whitespace-nowrap">
            View results →
          </Link>
        )}
      </td>
    </tr>
  );

  return (
    <div className="p-6">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-display text-xl text-ink-900">Run history</h2>
        <p className="text-xs text-ink-400">
          ★ Pin runs to mark them as named snapshots for comparison
        </p>
      </div>
      {latest.data && (
        <p className="text-xs text-ink-700 mb-3" data-testid="last-run-summary">
          Latest completed run{latest.data.run_completed_at ? ` (${new Date(latest.data.run_completed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })})` : ''}:{' '}
          {latest.data.kpis.priorities.P1} Act now {latest.data.kpis.priorities.P1 === 1 ? 'asset' : 'assets'} (Priority 1) · {latest.data.kpis.overall_high_plus.count} of {latest.data.kpis.overall_high_plus.of} High or above on Overall risk
        </p>
      )}

      <table className="w-full text-sm border border-ink-100 bg-white rounded-sm">
        <thead className="bg-ink-50/80 border-b border-ink-100">
          <tr>
            <th className="px-3 py-2 w-8"></th>
            <th className="text-left text-[11px] font-medium text-ink-500 uppercase tracking-wider px-3 py-2">Run / Label</th>
            <th className="text-left text-[11px] font-medium text-ink-500 uppercase tracking-wider px-3 py-2">Status</th>
            <th className="text-left text-[11px] font-medium text-ink-500 uppercase tracking-wider px-3 py-2">Type</th>
            <th className="text-left text-[11px] font-medium text-ink-500 uppercase tracking-wider px-3 py-2">Modules</th>
            <th className="text-right text-[11px] font-medium text-ink-500 uppercase tracking-wider px-3 py-2">Assets</th>
            <th className="text-right text-[11px] font-medium text-ink-500 uppercase tracking-wider px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {pinned.length > 0 && (
            <>
              <tr>
                <td colSpan={7} className="px-3 py-1.5 bg-amber-50/60 text-[10px] font-medium text-amber-700 uppercase tracking-wide border-b border-amber-100">
                  ★ Pinned snapshots
                </td>
              </tr>
              {pinned.map((r) => <RunRow key={r.id} r={r} />)}
              {unpinned.length > 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-1.5 bg-ink-50/60 text-[10px] font-medium text-ink-400 uppercase tracking-wide border-b border-ink-100">
                    All runs
                  </td>
                </tr>
              )}
            </>
          )}
          {unpinned.map((r) => <RunRow key={r.id} r={r} />)}
        </tbody>
      </table>
    </div>
  );
}

function RunStatusBadge({ status }: { status: string }) {
  const cls =
    status === 'Completed' ? 'bg-risk-low/10 text-risk-low'
    : status === 'Failed'  ? 'bg-risk-very_high/10 text-risk-very_high'
    : 'bg-risk-moderate/10 text-risk-moderate';
  return (
    <span className={`px-1.5 py-0.5 text-[10px] rounded-sm font-semibold uppercase tracking-wider ${cls}`}>
      {status}
    </span>
  );
}

function SettingsPanel({ project }: { project: Project }) {
  return (
    <div className="p-6 max-w-5xl">
      <h2 className="font-display text-xl text-ink-900 mb-4">Settings</h2>
      <dl className="space-y-3 text-sm">
        <div className="grid grid-cols-[180px_1fr] gap-2">
          <dt className="text-ink-500">Project ID</dt>
          <dd className="text-ink-700 font-mono text-xs">{project.id}</dd>
        </div>
        <div className="grid grid-cols-[180px_1fr] gap-2">
          <dt className="text-ink-500">Project level</dt>
          <dd className="text-ink-700">{project.project_level === 'Business_Unit' ? 'Business unit' : project.project_level}</dd>
        </div>
        <ProjectStatusRow project={project} />
      </dl>
      <OrgStructurePanel project={project} />
      <VendorReadinessToggle project={project} />
      <ReportingProfileSettings projectId={project.id} />
      <ProgrammeAssumptionsSettings projectId={project.id} />

      <DangerZone projectId={project.id} projectName={project.name} />
    </div>
  );
}

/** Optional Vendor readiness module (C-107): per-project switch; off for new projects. */
const STATUS_LABEL: Record<string, string> = { Draft: 'Draft', Active: 'Active', On_hold: 'On hold', Completed: 'Completed', Archived: 'Archived', Cancelled: 'Cancelled' };

/** Project status (HO-05): Completed, Archived and Cancelled freeze the asset register; owners and administrators set it. */
function ProjectStatusRow({ project }: { project: Project }) {
  const auth = useAuth();
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (status: string) => api.put<{ status: string }>(`/projects/${project.id}/status`, { status }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['project', project.id] }); },
  });
  const canEdit = auth.hasRole('admin', 'super_user', 'analyst');
  return (
    <div className="grid grid-cols-[180px_1fr] gap-2">
      <dt className="text-ink-500">Status</dt>
      <dd className="text-ink-700">
        {canEdit ? (
          <select value={project.status} disabled={save.isPending} onChange={(e) => save.mutate(e.target.value)} aria-label="Project status" data-testid="project-status">
            {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        ) : (STATUS_LABEL[project.status] ?? project.status)}
        <span className="block text-xs text-ink-500 mt-0.5">Completed, Archived and Cancelled freeze the asset register: imports and enrolment are refused until the project is reopened.</span>
        {save.isError && <span className="block text-xs text-red-700 mt-0.5">{save.error instanceof Error ? save.error.message : 'Could not change the status.'}</span>}
      </dd>
    </div>
  );
}

function VendorReadinessToggle({ project }: { project: Project }) {
  const auth = useAuth();
  const qc = useQueryClient();
  const canEdit = auth.hasRole('admin', 'super_user', 'analyst');
  const settingQ = useQuery({
    queryKey: ['vendor-readiness-setting', project.id],
    queryFn: () => api.get<{ enabled: boolean; public_form_enabled: boolean }>(`/projects/${project.id}/vendor-readiness-setting`),
  });
  const save = useMutation({
    mutationFn: (enabled: boolean) =>
      api.put<{ enabled: boolean; public_form_enabled: boolean }>(`/projects/${project.id}/vendor-readiness-setting`, { enabled }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vendor-readiness-setting', project.id] });
      void qc.invalidateQueries({ queryKey: ['project', project.id] });
    },
  });
  const enabled = settingQ.data?.enabled ?? project.vendor_readiness_enabled === true;
  return (
    <section className="mt-8 pt-6 border-t border-ink-100" data-testid="vendor-readiness-toggle">
      <h3 className="text-sm font-medium text-ink-900 mb-2">Optional modules</h3>
      <label className="flex items-start gap-3 text-sm text-ink-700">
        <input
          type="checkbox"
          className="h-4 w-4 mt-0.5"
          checked={enabled}
          disabled={!canEdit || save.isPending || settingQ.isLoading}
          onChange={(e) => save.mutate(e.target.checked)}
          aria-label="Vendor readiness questionnaire (optional)"
        />
        <span>
          <span className="font-medium">Vendor readiness questionnaire (optional)</span>
          <span className="block text-xs text-ink-500 mt-0.5">
            Adds the Vendor readiness tab: send suppliers a post-quantum readiness questionnaire and
            feed confirmed answers into the Supply chain inputs of the assets they supply. Not needed
            for a risk assessment. Turning it off hides the tab and keeps any answers already recorded.
          </span>
          <span className="block text-xs text-ink-500 mt-1">
            {settingQ.data?.public_form_enabled
              ? 'The online vendor form is enabled on this installation.'
              : 'The online vendor form (vendors answering through a link) is off on this installation; vendors return the questionnaire as a spreadsheet. An administrator can enable it with VPR_PUBLIC_FORM_ENABLED=true.'}
          </span>
        </span>
      </label>
      {save.error && <p className="text-xs text-accent mt-2">{(save.error as Error).message}</p>}
    </section>
  );
}

function DangerZone({ projectId, projectName }: { projectId: string; projectName: string }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showDialog, setShowDialog] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const del = useMutation({
    mutationFn: () => api.delete(`/projects/${projectId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      navigate('/');
    },
  });

  if (!auth.hasRole('admin')) return null;

  return (
    <section className="mt-10 pt-6 border-t border-accent/30">
      <h3 className="text-sm font-medium text-accent mb-2">Danger zone</h3>
      <p className="text-sm text-ink-500 mb-3">
        {/* Matches the schema's cascade from project (verified against information_schema, C-031). */}
        Deleting a project removes its asset enrolments (the assets stay in the inventory), every
        assessment run with its results and findings, the cryptographic artifacts recorded in this
        project, compliance assertions and policy assignments, the organisational readiness (gap
        assessment) answers, the remediation backlog, vendor commitments, the reporting profile,
        parameter values and members. Vendor readiness questionnaires are not deleted.
        This cannot be undone.
      </p>
      <button
        type="button"
        onClick={() => setShowDialog(true)}
        className="px-3 py-1.5 text-sm border border-accent text-accent rounded-sm hover:bg-accent hover:text-white transition-colors"
      >
        Delete project
      </button>

      {showDialog && (
        <div
          className="fixed inset-0 z-40 bg-ink-900/30 flex items-center justify-center p-4"
          onClick={() => setShowDialog(false)}
        >
          <div className="bg-white rounded-md shadow-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-medium text-accent mb-3">Confirm project deletion</h3>
            <p className="text-sm text-ink-700 mb-4">
              You are about to permanently delete <strong>{projectName}</strong>.
              Type the project name below to confirm.
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full mb-3"
              placeholder={projectName}
              autoFocus
            />
            {del.error && <p className="text-accent text-sm mb-2">{(del.error as Error).message}</p>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowDialog(false)} className="btn-ghost">Cancel</button>
              <button
                type="button"
                disabled={confirmText !== projectName || del.isPending}
                onClick={() => del.mutate()}
                className="px-3 py-1.5 text-sm bg-accent text-white rounded-sm hover:bg-accent/90 disabled:bg-ink-200 disabled:text-ink-400"
              >
                {del.isPending ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}